import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Autocomplete,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Print, QrCode2 } from '@mui/icons-material';
import { DataService } from 'src/services/data-service';
import { toast } from 'react-toastify';
import Loader from 'src/components/Loader';
import { useJsBarcode } from '../shipment/shipmentDetailsComponent/BarcodeDisplay';

interface Shipment {
  id: string;
  label: string;
}

interface BoxItem {
  id: string;
  unitId: string;
  barcode: string;
  qty: number;
  unitDetails: any;
}

interface Box {
  id: string;
  refNumber: string;
  shipmentId: string;
  barcode: string;
  status: number;
  boxItems: BoxItem[];
  shipment: any;
  createdAt: string;
  updatedAt: string;
}

interface ShipmentBoxesResponse {
  success: boolean;
  message: string;
  data: {
    items: Box[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  token: string | null;
}

const PrintBarcodes: React.FC = () => {
  useJsBarcode(); // Load JsBarcode library

  const [manualBarcode, setManualBarcode] = useState('');
  const [printQuantity, setPrintQuantity] = useState(1);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [selectedBoxes, setSelectedBoxes] = useState<Set<string>>(new Set());
  const [loadingShipments, setLoadingShipments] = useState(true);
  const [loadingBoxes, setLoadingBoxes] = useState(false);

  useEffect(() => {
    fetchShipments();
  }, []);

  // Generate barcode as base64 image for printing
  const generateBarcodeForPrint = useCallback((value: string): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 300;

    const JsBarcode = (window as any).JsBarcode;
    if (JsBarcode) {
      try {
        JsBarcode(canvas, value, {
          format: 'CODE128',
          width: 2,
          height: 220,
          displayValue: false,
          margin: 0,
          background: '#ffffff',
          lineColor: '#000000',
        });
        return canvas.toDataURL('image/png');
      } catch (error) {
        console.warn('JsBarcode generation failed:', error);
      }
    }
    return '';
  }, []);

  const fetchShipments = async () => {
    setLoadingShipments(true);
    try {
      const response = await DataService.get('api/Lov/all-shipments');
      if (response.ok) {
        const data: Record<string, string> = await response.json();
        const shipmentList: Shipment[] = Object.entries(data).map(([id, label]) => ({
          id,
          label
        }));
        setShipments(shipmentList);
      } else {
        toast.error('Failed to fetch shipments');
      }
    } catch (error) {
      toast.error('Failed to fetch shipments');
    }
    setLoadingShipments(false);
  };

  const fetchBoxes = async (shipmentId: string) => {
    setLoadingBoxes(true);
    try {
      const response = await DataService.get(
        `api/Shipment/${shipmentId}/boxes?pageNumber=1&pageSize=50&includeUnitDetails=false`
      );
      if (response.ok) {
        const result: ShipmentBoxesResponse = await response.json();
        if (result.success) {
          setBoxes(result.data.items);
        } else {
          toast.error(result.message || 'Failed to fetch boxes');
        }
      } else {
        toast.error('Failed to fetch boxes');
      }
    } catch (error) {
      toast.error('Failed to fetch boxes');
    }
    setLoadingBoxes(false);
  };

  const handleShipmentChange = (event: any, newValue: Shipment | null) => {
    setSelectedShipment(newValue);
    setSelectedBoxes(new Set());
    setBoxes([]);

    if (newValue) {
      fetchBoxes(newValue.id);
    }
  };

  const handleBoxToggle = (boxId: string) => {
    const newSelected = new Set(selectedBoxes);
    if (newSelected.has(boxId)) {
      newSelected.delete(boxId);
    } else {
      newSelected.add(boxId);
    }
    setSelectedBoxes(newSelected);
  };

  const handleSelectAllBoxes = () => {
    if (selectedBoxes.size === boxes.length) {
      setSelectedBoxes(new Set());
    } else {
      setSelectedBoxes(new Set(boxes.map(box => box.id)));
    }
  };

  const handlePrintManualBarcode = () => {
    if (!manualBarcode.trim()) {
      toast.error('Please enter a barcode');
      return;
    }

    if (printQuantity < 1) {
      toast.error('Print quantity must be at least 1');
      return;
    }

    // Generate print window
    printBarcodes([{ barcode: manualBarcode, quantity: printQuantity }]);
  };

  const handlePrintSelectedBoxes = () => {
    if (selectedBoxes.size === 0) {
      toast.error('Please select at least one box');
      return;
    }

    const barcodesToPrint = boxes
      .filter(box => selectedBoxes.has(box.id))
      .map(box => ({ barcode: box.barcode, quantity: 1 }));

    printBarcodes(barcodesToPrint);
  };

  const printBarcodes = (barcodes: { barcode: string; quantity: number }[]) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Please allow popups to print barcodes');
      return;
    }

    let printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Barcodes</title>
        <style>
          @page {
            size: 50mm 25mm;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            width: 100%;
            background: #ffffff;
          }
          .page {
            width: 50mm;
            height: 25mm;
            display: flex;
            align-items: center;
            justify-content: center;
            page-break-after: always;
            background: #ffffff;
          }
          .barcode-wrap {
            width: 46mm;
            height: 21mm;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .barcode-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
            image-rendering: -webkit-optimize-contrast;
            image-rendering: crisp-edges;
          }
        </style>
      </head>
      <body>
    `;

    barcodes.forEach(({ barcode, quantity }) => {
      const barcodeImage = generateBarcodeForPrint(barcode);
      for (let i = 0; i < quantity; i++) {
        printContent += `
          <div class="page">
            <div class="barcode-wrap">
              <img src="${barcodeImage}" alt="Barcode" class="barcode-image" />
            </div>
          </div>
        `;
      }
    });

    printContent += `
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loadingShipments) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Print Barcodes
      </Typography>

      <Grid container spacing={3}>
        {/* Manual Barcode Entry Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCode2 /> Manual Barcode Entry
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Barcode"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e.target.value)}
                placeholder="Enter or scan barcode"
                autoFocus
              />

              <TextField
                fullWidth
                type="number"
                label="Print Quantity"
                value={printQuantity}
                onChange={(e) => setPrintQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<Print />}
                onClick={handlePrintManualBarcode}
                size="large"
              >
                Print Barcode
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {/* Shipment Selection Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Print from Shipment
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Autocomplete
              options={shipments}
              getOptionLabel={(option: any) => option.label}
              value={selectedShipment}
              onChange={handleShipmentChange}
              renderInput={(params) => (
                <TextField {...params} label="Select Shipment" placeholder="Choose a shipment" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Paper>
        </Grid>

        {/* Boxes Section */}
        {selectedShipment && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Boxes in Shipment: {selectedShipment.label}
                </Typography>
                {boxes.length > 0 && (
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handleSelectAllBoxes}
                      sx={{ mr: 2 }}
                    >
                      {selectedBoxes.size === boxes.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Print />}
                      onClick={handlePrintSelectedBoxes}
                      disabled={selectedBoxes.size === 0}
                    >
                      Print Selected ({selectedBoxes.size})
                    </Button>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {loadingBoxes ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : boxes.length === 0 ? (
                <Alert severity="info">No boxes found for this shipment</Alert>
              ) : (
                <Grid container spacing={2}>
                  {boxes.map((box) => (
                    <Grid item xs={12} sm={6} md={4} key={box.id}>
                      <Card
                        sx={{
                          border: selectedBoxes.has(box.id) ? '2px solid #1976d2' : '1px solid #ddd',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 3
                          }
                        }}
                        onClick={() => handleBoxToggle(box.id)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Ref: {box.refNumber}
                              </Typography>
                              <Typography variant="h6" sx={{ my: 1, fontFamily: 'monospace' }}>
                                {box.barcode}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {box.boxItems.length} item(s)
                              </Typography>
                            </Box>
                            <Checkbox
                              checked={selectedBoxes.has(box.id)}
                              onChange={() => handleBoxToggle(box.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PrintBarcodes;
